import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

export const customerPhoneMessage = '请输入有效手机号或带区号的固定电话；国际号码请加 +国家码，分机可写“转123”或“ext. 123”';
export type CustomerPhoneValidation = { valid: boolean; normalized?: string; message?: string };

function parseCustomerPhone(value: string) {
  if (typeof value !== 'string' || value.length > 60) return null;
  const source = value.normalize('NFKC').replace(/[‐‑‒–—−]/g, '-').trim();
  if (!source) return { normalized: '', aliases: [''] };
  const suffix = /\s*(?:转|分机|extension|ext\.?|x|#)\s*[:：]?\s*([0-9]{1,10})$/i.exec(source);
  const extension = suffix?.[1];
  let core = (suffix ? source.slice(0, suffix.index) : source).trim();
  // Do not let the parser extract a plausible number from arbitrary prose,
  // vanity letters, multiple numbers, or malformed extension notation.
  if (!/^\+?[0-9() .-]+$/.test(core) || !/[0-9]/.test(core) || !/^[+(0-9]/.test(core) || !/[0-9)]$/.test(core) || /[.-]\s*[.-]/.test(core)) return null;
  let depth = 0, groups = 0;
  for (const char of core) {
    if (char === '(' && (++depth > 1 || ++groups > 1)) return null;
    if (char === ')' && --depth !== 0) return null;
  }
  if (depth !== 0 || /\([^0-9)]*\)/.test(core) || /\([^)]*[.-]/.test(core)) return null;
  const international = core.startsWith('+') || core.startsWith('00');
  if (core.startsWith('00')) core = '+' + core.slice(2);
  const phone = parsePhoneNumberFromString(core, { defaultCountry: 'CN', extract: false });
  if (!phone?.isValid()) return null;
  const national = phone.formatNational({ formatExtension: () => '' }).replace(/\D/g, '');
  // Unprefixed numbers must be complete domestic numbers, including the trunk
  // zero on fixed lines. An international country code must use + or 00.
  if (!international && (phone.country !== 'CN' || core.replace(/\D/g, '') !== national)) return null;
  const normalizedBase = phone.country === 'CN' ? national : phone.number;
  const normalized = normalizedBase + (extension ? ` ext. ${extension}` : '');
  const bases = [...new Set([normalizedBase, phone.number, '00' + phone.number.slice(1), phone.number.slice(1)])];
  const aliases = [value.trim(), source, source.replace(/[\s()（）-]/g, ''), normalized];
  for (const base of bases) {
    if (!extension) aliases.push(base);
    else for (const marker of [' ext. ', 'ext.', 'ext', '转', '分机', 'x', '#']) aliases.push(base + marker + extension);
  }
  return { normalized, aliases: [...new Set(aliases)] };
}

export function validateCustomerPhone(value: string): CustomerPhoneValidation {
  const parsed = parseCustomerPhone(value);
  return parsed ? { valid: true, normalized: parsed.normalized } : { valid: false, message: customerPhoneMessage };
}

// Match canonical values and common pre-validation stored representations.
// Existing records are never rewritten as a side effect of reading or claiming.
export function customerPhoneLookupValues(value: string): string[] {
  return parseCustomerPhone(value)?.aliases.filter(Boolean) ?? [value.trim()].filter(Boolean);
}
